import { Loader2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

import { EnterpriseBrandingEditor } from "@/components/platform/EnterpriseBrandingEditor";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  createEnterprise,
  uploadEnterpriseLogo,
  uploadEnterpriseDarkLogo,
} from "@/services/platformEnterprisesService";

const QUERY_KEY = ["platform", "enterprises"] as const;

const SLUG_RE = /^[a-z][a-z0-9-]{1,62}[a-z0-9]$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ALLOWED_LOGO_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;

interface FormValues {
  name: string;
  subdomain: string;
  primaryColor: string;
  primaryColorDark: string;
  adminEmail: string;
}

interface FormErrors {
  name?: string;
  subdomain?: string;
  adminEmail?: string;
}

const emptyForm: FormValues = {
  name: "",
  subdomain: "",
  primaryColor: "#000000",
  primaryColorDark: "",
  adminEmail: "",
};

function validateForm(
  values: FormValues,
  t: (k: string) => string,
): FormErrors {
  const errors: FormErrors = {};
  if (!values.name.trim()) {
    errors.name = t("platformEnterprises.validationRequired");
  }
  if (!values.subdomain.trim()) {
    errors.subdomain = t("platformEnterprises.validationRequired");
  } else if (!SLUG_RE.test(values.subdomain.trim())) {
    errors.subdomain = t("platformEnterprises.validationSlugFormat");
  }
  if (!values.adminEmail.trim()) {
    errors.adminEmail = t("platformEnterprises.validationRequired");
  } else if (!EMAIL_RE.test(values.adminEmail.trim())) {
    errors.adminEmail = t("platformEnterprises.validationEmailFormat");
  }
  return errors;
}

export function EnterpriseNewPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormValues>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [darkLogoFile, setDarkLogoFile] = useState<File | null>(null);
  const [darkLogoPreview, setDarkLogoPreview] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (payload: {
      name: string;
      subdomain: string;
      primaryColor?: string;
      primaryColorDark?: string;
      adminEmail: string;
    }) => {
      let enterprise = await createEnterprise(payload);
      if (logoFile) {
        try {
          const { logoUrl } = await uploadEnterpriseLogo(enterprise.id, logoFile);
          enterprise = { ...enterprise, logoUrl };
        } catch {
          toast.error(t("platformEnterprises.logoUploadError"));
        }
      }
      if (darkLogoFile) {
        try {
          const { logoUrlDark } = await uploadEnterpriseDarkLogo(enterprise.id, darkLogoFile);
          enterprise = { ...enterprise, logoUrlDark };
        } catch {
          toast.error(t("platformEnterprises.logoUploadError"));
        }
      }
      return enterprise;
    },
    onSuccess: (enterprise) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success(t("platformEnterprises.createSuccess"));
      navigate(`/platform/enterprises/${enterprise.id}`);
    },
    onError: (err: unknown) => {
      const status =
        err != null &&
        typeof err === "object" &&
        "response" in err &&
        (err as { response?: { status?: number } }).response?.status;
      if (status === 409) {
        setServerError(t("platformEnterprises.errorDuplicate"));
      } else {
        toast.error(t("platformEnterprises.createError"));
      }
    },
  });

  const handleField = useCallback(
    (field: keyof FormValues) =>
      (e: React.ChangeEvent<HTMLInputElement>): void => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setErrors((prev) => ({ ...prev, [field]: undefined }));
        setServerError(null);
      },
    [],
  );

  const handleSubmit = useCallback((): void => {
    const validationErrors = validateForm(form, t);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setServerError(null);
    saveMutation.mutate({
      name: form.name.trim(),
      subdomain: form.subdomain.trim(),
      primaryColor: form.primaryColor || undefined,
      primaryColorDark: form.primaryColorDark.trim() || undefined,
      adminEmail: form.adminEmail.trim(),
    });
  }, [form, saveMutation, t]);

  const isSaving = saveMutation.isPending;

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title={t("platformEnterprises.newPageTitle")}
        description={t("platformEnterprises.newPageDescription")}
        onBack={() => navigate("/platform/enterprises")}
      />

      <div className="mt-4 space-y-4">
        <Card className="shadow-sm">
          <CardContent className="p-6 space-y-5">
            {serverError != null && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/80 dark:bg-red-950/40 dark:text-red-300">
                {serverError}
              </div>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="name">
                  {t("platformEnterprises.fieldName")}
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={handleField("name")}
                  disabled={isSaving}
                  placeholder="Acme Corp"
                />
                {errors.name != null && (
                  <p className="text-xs text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="subdomain">
                  {t("platformEnterprises.fieldSubdomain")}
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <Input
                  id="subdomain"
                  value={form.subdomain}
                  onChange={handleField("subdomain")}
                  disabled={isSaving}
                  placeholder="acme"
                  className="font-mono"
                />
                {errors.subdomain != null && (
                  <p className="text-xs text-destructive">{errors.subdomain}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t("platformEnterprises.fieldSubdomainHint")}
                </p>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="adminEmail">
                  {t("platformEnterprises.fieldAdminEmail")}
                  <span className="ml-1 text-destructive">*</span>
                </Label>
                <Input
                  id="adminEmail"
                  type="email"
                  value={form.adminEmail}
                  onChange={handleField("adminEmail")}
                  disabled={isSaving}
                  placeholder="admin@acme.com"
                />
                {errors.adminEmail != null && (
                  <p className="text-xs text-destructive">{errors.adminEmail}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {t("platformEnterprises.fieldAdminEmailHint")}
                </p>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label>{t("platformEnterprises.brandingSectionTitle")}</Label>
                <EnterpriseBrandingEditor
                  enterpriseName={form.name || t("platformEnterprises.brandingPlaceholderName")}
                  disabled={isSaving}
                  allowedLogoMimeTypes={ALLOWED_LOGO_TYPES}
                  maxLogoBytes={MAX_LOGO_SIZE_BYTES}
                  onSizeError={() => toast.error(t("platformEnterprises.logoSizeError"))}
                  onFormatError={() => toast.error(t("platformEnterprises.logoFormatError"))}
                  light={{
                    existingLogoSrc: null,
                    pendingLogoPreview: logoPreview,
                    logoLoadError: false,
                    color: form.primaryColor,
                    onPickFile: (file) => {
                      setLogoFile(file);
                      setLogoPreview(URL.createObjectURL(file));
                    },
                    onClearPendingFile: () => {
                      if (logoPreview) URL.revokeObjectURL(logoPreview);
                      setLogoFile(null);
                      setLogoPreview(null);
                    },
                    onColorChange: (hex) =>
                      setForm((prev) => ({ ...prev, primaryColor: hex })),
                    onLogoLoadError: () => undefined,
                  }}
                  dark={{
                    existingLogoSrc: null,
                    pendingLogoPreview: darkLogoPreview,
                    logoLoadError: false,
                    color: form.primaryColorDark,
                    colorEmpty: !form.primaryColorDark,
                    onPickFile: (file) => {
                      setDarkLogoFile(file);
                      setDarkLogoPreview(URL.createObjectURL(file));
                    },
                    onClearPendingFile: () => {
                      if (darkLogoPreview) URL.revokeObjectURL(darkLogoPreview);
                      setDarkLogoFile(null);
                      setDarkLogoPreview(null);
                    },
                    onColorChange: (hex) =>
                      setForm((prev) => ({ ...prev, primaryColorDark: hex })),
                    onLogoLoadError: () => undefined,
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardContent className="flex flex-row flex-wrap items-center justify-between gap-3 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/platform/enterprises")}
              disabled={isSaving}
              className="transition-colors duration-150"
            >
              {t("pages.common.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="transition-colors duration-150"
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
                  {t("platformEnterprises.saving")}
                </>
              ) : (
                t("platformEnterprises.save")
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
